import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatTabsModule } from '@angular/material/tabs'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field'
import { MatInput, MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule } from '@angular/material/core'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask'
import moment from 'moment/moment'
import { firstValueFrom } from 'rxjs'

import { SupplierPortalService } from '../../../core/services/supplier-portal/supplier-portal.service'
import { VehicleModelService } from '../../../core/services/vehicle/vehicle-model/vehicle-model.service'
import { VehicleBrandService } from '../../../core/services/vehicle/vehicle-brand/vehicle-brand.service'
import { VehicleTypeService } from '../../../core/services/vehicle/vehicle-type/vehicle-type.service'
import { DocumentTypeService } from '../../../core/services/document/document-type/document-type.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { FornecedorDocumentoUpload, FornecedorVeiculoVm } from '../../../core/models/supplier-portal.model'
import { ErrorRequestModel } from '../../../core/models/response.model'
import { ModalVehicleEditComponent } from '../modal-vehicle-edit/modal-vehicle-edit.component'
import { ModalSupplierPortalDocumentsComponent } from '../modal-supplier-portal-documents/modal-supplier-portal-documents.component'
import { DocumentTypeOption, loadAcceptedDocumentTypes } from '../supplier-portal-document-type.util'

export interface AssignVehicleResult {
  success: boolean
  avisos: string[]
}

interface PendingDocument {
  descricao: string
  tipoDocId: number
  tipoDocDescricao: string
  arquivo: File
}

// Veículo é 1:N exclusivo (pertence a um único fornecedor), diferente do motorista
// (N:N). Por isso essa modal só tem 2 abas: os meus veículos e cadastrar novo —
// não existe "buscar veículo de outro fornecedor" porque não faz sentido no modelo.
@Component({
  selector: 'modal-assign-vehicle',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatFormField, MatInput, MatLabel,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinner,
    MatTooltipModule,
    NgxMaskDirective,
  ],
  templateUrl: './modal-assign-vehicle.component.html',
  styleUrl: './modal-assign-vehicle.component.scss',
  providers: [provideNgxMask()],
})
export class ModalAssignVehicleComponent implements OnInit {
  @Output() response = new EventEmitter<AssignVehicleResult>()

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: { osId: string },
    private dialogRef: MatDialogRef<ModalAssignVehicleComponent>,
    private dialog: MatDialog,
    private supplierPortalService: SupplierPortalService,
    private vehicleModelService: VehicleModelService,
    private vehicleBrandService: VehicleBrandService,
    private vehicleTypeService: VehicleTypeService,
    private documentTypeService: DocumentTypeService,
    private snackbar: SnackbarCustomService,
  ) {}

  vehicleModels: any[] = []
  vehicleBrands: any[] = []
  vehicleTypes: any[] = []
  // Filtro de UI: modelo só é selecionável depois de escolher a marca (fluxo
  // recomendado pela API — não é enviado no payload, só reduz a lista do select).
  marcaControl = new FormControl<number | null>(null)

  get filteredVehicleModels(): any[] {
    const marcaId = this.marcaControl.value
    if (!marcaId) return []
    return this.vehicleModels.filter((item: any) => item.veiculoMarcaId === marcaId)
  }

  // Aba "Meus veículos"
  myVehicles: FornecedorVeiculoVm[] = []
  loadingList = false
  assigningId: string | null = null

  // Aba "Cadastrar novo"
  form!: FormGroup
  creating = false

  // Documentos anexados durante o cadastro — ainda não existe veiculoId, então ficam
  // "staged" localmente e só são enviados (em sequência) depois que o veículo é criado.
  tipoDocOptions: DocumentTypeOption[] = []
  pendingDocuments: PendingDocument[] = []
  pendingDescricao = ''
  pendingTipoDocId: number | null = null
  pendingFile: File | null = null

  private readonly currentYear = new Date().getFullYear()

  ngOnInit(): void {
    this.createForm()
    this.loadVehicleModels()
    this.loadVehicleBrands()
    this.loadVehicleTypes()
    this.loadMyVehicles()
    this.loadDocumentTypes()
    this.marcaControl.valueChanges.subscribe(() => {
      this.form.get('veiculoModeloId')?.setValue(null)
    })
  }

  private loadDocumentTypes(): void {
    loadAcceptedDocumentTypes(this.documentTypeService, 'veiculo').subscribe({
      next: (options) => { this.tipoDocOptions = options },
      error: () => { this.tipoDocOptions = [] },
    })
  }

  onPendingFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    this.pendingFile = input.files && input.files.length > 0 ? input.files[0] : null
  }

  addPendingDocument(): void {
    if (!this.pendingDescricao || !this.pendingTipoDocId || !this.pendingFile) return
    const tipo = this.tipoDocOptions.find((t) => t.id === this.pendingTipoDocId)
    this.pendingDocuments.push({
      descricao: this.pendingDescricao,
      tipoDocId: this.pendingTipoDocId,
      tipoDocDescricao: tipo?.descricao ?? '',
      arquivo: this.pendingFile,
    })
    this.pendingDescricao = ''
    this.pendingTipoDocId = null
    this.pendingFile = null
  }

  removePendingDocument(index: number): void {
    this.pendingDocuments.splice(index, 1)
  }

  // Sequência pedida pelo backend: cria o veículo, pega o id da resposta, só então
  // envia os documentos um a um — upload de documento depende do id não existir antes.
  private async uploadPendingDocuments(veiculoId: string): Promise<void> {
    for (const doc of this.pendingDocuments) {
      const upload = new FornecedorDocumentoUpload()
      upload.Descricao = doc.descricao
      upload.TipoDocId = doc.tipoDocId
      upload.Arquivo = doc.arquivo
      try {
        await firstValueFrom(this.supplierPortalService.uploadVeiculoDocumento(veiculoId, upload.toFormData()))
      } catch {
        this.snackbar.warning(`Veículo cadastrado, mas falha ao enviar o documento "${doc.descricao}". Anexe novamente pela edição.`)
      }
    }
  }

  private createForm(): void {
    this.form = this.fb.group({
      veiculoModeloId: [null, Validators.required],
      tipoVeiculoId: [null, Validators.required],
      placa: ['', Validators.required],
      cor: ['', Validators.required],
      ano: [null, [Validators.min(1950), Validators.max(this.currentYear + 1)]],
      crlv: ['', Validators.required],
      seguro: [''],
      seguroValidade: [''],
    })
  }

  private loadVehicleModels(): void {
    this.vehicleModelService.listVehicleModel().subscribe({
      next: (res: any) => { this.vehicleModels = res.data || [] },
    })
  }

  private loadVehicleBrands(): void {
    this.vehicleBrandService.listVehicleBrand().subscribe({
      next: (res: any) => { this.vehicleBrands = res.data || [] },
    })
  }

  private loadVehicleTypes(): void {
    this.vehicleTypeService.listVehicleType().subscribe({
      next: (res: any) => { this.vehicleTypes = res.data || [] },
    })
  }

  loadMyVehicles(): void {
    this.loadingList = true
    this.supplierPortalService.listPagedVeiculos(1, 50).subscribe({
      next: (res: any) => { this.myVehicles = res.data ?? [] },
      error: (err: ErrorRequestModel) => { this.loadingList = false; this.snackbar.errorRequest(err) },
      complete: () => { this.loadingList = false },
    })
  }

  assignExisting(veiculoId: string): void {
    this.assigningId = veiculoId
    this.supplierPortalService.atribuirVeiculo(this.data.osId, veiculoId).subscribe({
      next: (res: any) => {
        this.snackbar.success(res.message)
        this.response.emit({ success: true, avisos: res.data?.avisos ?? [] })
        this.dialogRef.close()
      },
      error: (err: ErrorRequestModel) => { this.assigningId = null; this.snackbar.errorRequest(err) },
    })
  }

  createAndAssign(): void {
    if (this.form.invalid) return
    this.creating = true
    const v = this.form.value
    const model = {
      veiculoModeloId: v.veiculoModeloId,
      tipoVeiculoId: v.tipoVeiculoId,
      placa: (v.placa || '').toUpperCase(),
      cor: v.cor,
      ano: v.ano ?? null,
      crlv: v.crlv || '',
      seguro: v.seguro || '',
      seguroValidade: v.seguroValidade ? moment(v.seguroValidade).format('DD/MM/YYYY') : '',
    }
    this.supplierPortalService.createVeiculo(model).subscribe({
      next: (res: any) => {
        const veiculoId = res.data?.id
        if (veiculoId) {
          // Sequência: cria → pega o id da resposta → sobe os documentos pendentes → atribui.
          this.uploadPendingDocuments(veiculoId).then(() => {
            this.creating = false
            this.assignExisting(veiculoId)
          })
        } else {
          this.creating = false
          this.snackbar.success(res.message)
          this.response.emit({ success: true, avisos: [] })
          this.dialogRef.close()
        }
      },
      // 409 = placa duplicada; errorRequest já extrai a mensagem do backend
      error: (err: ErrorRequestModel) => { this.creating = false; this.snackbar.errorRequest(err) },
    })
  }

  editVehicle(vehicle: FornecedorVeiculoVm): void {
    const dialogRef = this.dialog.open(ModalVehicleEditComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '1200px',
      maxWidth: '95vw',
      data: { veiculo: vehicle },
    })
    dialogRef.componentInstance.response.subscribe((success: boolean) => {
      if (success) this.loadMyVehicles()
    })
  }

  openDocuments(vehicle: FornecedorVeiculoVm): void {
    this.dialog.open(ModalSupplierPortalDocumentsComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '800px',
      maxWidth: '800px',
      data: { escopo: 'veiculo', ownerId: vehicle.id, titulo: `${vehicle.placa} — ${vehicle.modelo}` },
    })
  }

  close(): void { this.dialogRef.close() }
}
